/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/PrevisionPage.tsx
   Dashboard de Previsión de Profesores
   ────────────────────────────────────────────────────────────── */
import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Stack,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import * as previsionApi from '../../../api/prevision';
import { useCentros } from '../../../hooks/useCentros';
import ProfesoresModal from '../../../components/prevision/ProfesoresModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prevision-tabpanel-${index}`}
      aria-labelledby={`prevision-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function getWeekStart(date: dayjs.Dayjs) {
  const day = date.day();
  const diff = day === 0 ? -6 : 1 - day;
  return date.startOf('day').add(diff, 'day');
}

type EstadoCapacidad = 'sinCapacidad' | 'critico' | 'alto' | 'normal' | 'bajo';

function getOcupacionReferencia(
  porcentaje: number | null | undefined,
  porcentajeEfectivo: number | null | undefined,
) {
  const valor = porcentajeEfectivo ?? porcentaje;
  return valor === null || valor === undefined ? null : valor;
}

function getEstadoCapacidad(
  porcentaje: number | null | undefined,
  porcentajeEfectivo: number | null | undefined,
  sinCapacidad?: boolean,
): EstadoCapacidad {
  if (sinCapacidad) {
    return 'sinCapacidad';
  }

  const valor = getOcupacionReferencia(porcentaje, porcentajeEfectivo);
  if (valor === null) {
    return 'sinCapacidad';
  }

  if (valor >= 95) {
    return 'critico';
  }
  if (valor >= 90) {
    return 'alto';
  }
  if (valor >= 50) {
    return 'normal';
  }
  return 'bajo';
}

function getEstadoChipColor(estado: EstadoCapacidad): 'error' | 'warning' | 'success' | 'default' {
  if (estado === 'sinCapacidad' || estado === 'critico') {
    return 'error';
  }
  if (estado === 'alto') {
    return 'warning';
  }
  if (estado === 'normal') {
    return 'success';
  }
  return 'default';
}

function getEstadoProgressColor(estado: EstadoCapacidad): 'error' | 'warning' | 'primary' {
  if (estado === 'sinCapacidad' || estado === 'critico') {
    return 'error';
  }
  if (estado === 'alto') {
    return 'warning';
  }
  return 'primary';
}

function shouldShowCriticalAlert(
  porcentaje: number | null | undefined,
  porcentajeEfectivo: number | null | undefined,
  sinCapacidad?: boolean,
) {
  if (sinCapacidad) {
    return false;
  }
  const valor = getOcupacionReferencia(porcentaje, porcentajeEfectivo);
  if (valor === null) {
    return false;
  }
  return valor >= 90;
}

interface TipoRowProps {
  tipo: previsionApi.ResumenTipo;
  fechaBase: string;
  centrosMap: Map<number, string>;
}

interface CentroRowProps {
  tipoId: number;
  centro: previsionApi.DesgloseCentro;
  centroNombreReal: string;
  fechaBase: string;
}

function TipoRow({ tipo, fechaBase, centrosMap }: TipoRowProps) {
  const { t } = useTranslation<'translation'>();
  const [open, setOpen] = useState(false);

  const { data: analisisAsignaturas, isLoading } = useQuery({
    queryKey: ['prevision', 'asignaturas', tipo.tipoAsignaturaId, fechaBase],
    queryFn: () => previsionApi.getAnalisisAsignaturas(tipo.tipoAsignaturaId, undefined, fechaBase),
    enabled: open,
  });

  const porcentajeTipo = tipo.porcentajeOcupacion ?? null;
  const porcentajeTipoEfectiva = tipo.porcentajeOcupacionEfectiva ?? null;
  const estado = getEstadoCapacidad(porcentajeTipo, porcentajeTipoEfectiva, tipo.sinCapacidad);

  const mostrarAlerta = shouldShowCriticalAlert(
    porcentajeTipo,
    tipo.porcentajeOcupacionEfectiva,
    tipo.sinCapacidad,
  );

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mostrarAlerta && <WarningIcon color="error" fontSize="small" />}
            <Typography variant="body2">{tipo.tipoAsignaturaNombre}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <ProfesoresCount
            count={tipo.profesoresActivos}
            ids={tipo.profesoresIds}
            label={`Profesores - ${tipo.tipoAsignaturaNombre}`}
          />
        </TableCell>
        <TableCell align="right">
          <Box sx={{ minWidth: 100 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {porcentajeTipo === null ? '—' : `${porcentajeTipo.toFixed(1)}%`}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={porcentajeTipo === null ? 0 : Math.min(porcentajeTipo, 100)}
              color={getEstadoProgressColor(estado)}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={t(`prevision.estado.${estado}`)}
            color={getEstadoChipColor(estado)}
            icon={
              estado === 'critico' || estado === 'alto' ? (
                <TrendingUpIcon />
              ) : estado === 'bajo' ? (
                <TrendingDownIcon />
              ) : undefined
            }
          />
        </TableCell>
        <TableCell align="right">{tipo.demandaProyectada.toFixed(0)}h/sem</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t('prevision.dashboard.tabla.asignaturas')}
              </Typography>
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={48} />
                      <TableCell>{t('prevision.dashboard.tabla.asignatura')}</TableCell>
                      <TableCell align="right">
                        {t('prevision.dashboard.tabla.profesores')}
                      </TableCell>
                      <TableCell align="right">{t('prevision.dashboard.tabla.horas')}</TableCell>
                      <TableCell align="right">
                        {t('prevision.dashboard.tabla.ocupacion')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analisisAsignaturas?.asignaturas.map((asig) => (
                      <AsignaturaRow
                        key={asig.asignaturaId}
                        asignatura={asig}
                        centrosMap={centrosMap}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

interface FranjaRowProps {
  franja: previsionApi.DesgloseFranja;
}

interface AsignaturaRowProps {
  asignatura: previsionApi.AsignaturaAnalisis;
  centrosMap: Map<number, string>;
}

interface AsignaturaCentroRowProps {
  centro: previsionApi.DesgloseCentro;
  centroNombreReal: string;
}

function AsignaturaCentroRow({ centro, centroNombreReal }: AsignaturaCentroRowProps) {
  const { t } = useTranslation<'translation'>();
  const [open, setOpen] = useState(false);

  const franjas = centro.franjas || [];
  const hasFranjas = franjas.length > 0;

  const porcentajeCentro = centro.porcentajeOcupacion ?? null;
  const porcentajeCentroEfectiva = centro.porcentajeOcupacionEfectiva ?? null;
  const estadoCentro = getEstadoCapacidad(
    porcentajeCentro,
    porcentajeCentroEfectiva,
    centro.sinCapacidad,
  );

  const mostrarAlerta = shouldShowCriticalAlert(
    porcentajeCentro,
    centro.porcentajeOcupacionEfectiva,
    centro.sinCapacidad,
  );

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          {hasFranjas && (
            <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mostrarAlerta && <WarningIcon color="error" fontSize="small" />}
            <Typography variant="body2">{centroNombreReal}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <ProfesoresCount
            count={centro.profesoresActivos}
            ids={centro.profesoresIds}
            label={`Profesores - ${centroNombreReal}`}
          />
        </TableCell>
        <TableCell align="right">
          {porcentajeCentro === null ? '—' : `${porcentajeCentro.toFixed(1)}%`}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={t(`prevision.estado.${estadoCentro}`)}
            color={getEstadoChipColor(estadoCentro)}
          />
        </TableCell>
      </TableRow>
      {hasFranjas && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, ml: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="50px"></TableCell>
                      <TableCell>{t('prevision.dashboard.tabla.franja')}</TableCell>
                      <TableCell align="right">
                        {t('prevision.dashboard.tabla.profesores')}
                      </TableCell>
                      <TableCell align="right">{t('prevision.dashboard.tabla.horas')}</TableCell>
                      <TableCell align="right">
                        {t('prevision.dashboard.tabla.ocupacion')}
                      </TableCell>
                      <TableCell>{t('prevision.dashboard.tabla.estadoCapacidad')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {franjas.map((franja) => (
                      <FranjaRow key={franja.franjaHoraria} franja={franja} />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function AsignaturaRow({ asignatura, centrosMap }: AsignaturaRowProps) {
  const { t } = useTranslation<'translation'>();
  const [open, setOpen] = useState(false);

  const porcentaje = asignatura.porcentajeOcupacion ?? null;
  const porcentajeEfectivo = asignatura.porcentajeOcupacionEfectiva ?? null;
  const estado = getEstadoCapacidad(porcentaje, porcentajeEfectivo, asignatura.sinCapacidad);

  const mostrarAlerta = shouldShowCriticalAlert(
    porcentaje,
    asignatura.porcentajeOcupacionEfectiva,
    asignatura.sinCapacidad,
  );
  const hasCentros = !!asignatura.desglosePorCentro && asignatura.desglosePorCentro.length > 0;
  const hasFranjas = !!asignatura.desglosePorFranja && asignatura.desglosePorFranja.length > 0;
  const canExpand = hasCentros || hasFranjas;

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          {canExpand && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mostrarAlerta && <WarningIcon color="error" fontSize="small" />}
            <Typography variant="body2">{asignatura.asignaturaNombre}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <ProfesoresCount
            count={asignatura.profesoresDisponibles}
            ids={asignatura.profesoresIds}
            label={`Profesores - ${asignatura.asignaturaNombre}`}
          />
        </TableCell>
        <TableCell align="right">
          {asignatura.sinCapacidad
            ? t('prevision.estado.sinCapacidad')
            : `${asignatura.horasReservadas.toFixed(1)}h / ${asignatura.horasDisponibles.toFixed(1)}h`}
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">
              {asignatura.sinCapacidad || porcentaje === null ? '—' : `${porcentaje.toFixed(1)}%`}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={asignatura.sinCapacidad || porcentaje === null ? 0 : Math.min(porcentaje, 100)}
              sx={{ width: 60 }}
              color={getEstadoProgressColor(estado)}
            />
          </Box>
        </TableCell>
      </TableRow>
      {canExpand && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                {hasCentros && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={48} />
                        <TableCell>{t('prevision.dashboard.tabla.centro')}</TableCell>
                        <TableCell align="right">
                          {t('prevision.dashboard.tabla.profesores')}
                        </TableCell>
                        <TableCell align="right">
                          {t('prevision.dashboard.tabla.ocupacion')}
                        </TableCell>
                        <TableCell>{t('prevision.dashboard.tabla.estadoCapacidad')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {asignatura.desglosePorCentro?.map((centro) => (
                        <AsignaturaCentroRow
                          key={`${asignatura.asignaturaId}-${centro.centroId}`}
                          centro={centro}
                          centroNombreReal={centrosMap.get(centro.centroId) || centro.centroNombre}
                        />
                      ))}
                    </TableBody>
                  </Table>
                )}
                {hasFranjas && (
                  <Box sx={{ mt: hasCentros ? 2 : 0 }}>
                    {hasCentros && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        {t('prevision.dashboard.tabla.franja')}
                      </Typography>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={50}></TableCell>
                          <TableCell>{t('prevision.dashboard.tabla.franja')}</TableCell>
                          <TableCell align="right">
                            {t('prevision.dashboard.tabla.profesores')}
                          </TableCell>
                          <TableCell align="right">
                            {t('prevision.dashboard.tabla.horas')}
                          </TableCell>
                          <TableCell align="right">
                            {t('prevision.dashboard.tabla.ocupacion')}
                          </TableCell>
                          <TableCell>{t('prevision.dashboard.tabla.estadoCapacidad')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {asignatura.desglosePorFranja?.map((franja) => (
                          <FranjaRow key={franja.franjaHoraria} franja={franja} />
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function FranjaRow({ franja }: FranjaRowProps) {
  const { t } = useTranslation<'translation'>();
  const [open, setOpen] = useState(false);

  const porcentajeFranja = franja.porcentajeOcupacion ?? null;
  const porcentajeFranjaEfectiva = franja.porcentajeOcupacionEfectiva ?? null;
  const estadoFranja = getEstadoCapacidad(
    porcentajeFranja,
    porcentajeFranjaEfectiva,
    franja.sinCapacidad,
  );

  const hasDias = franja.dias && franja.dias.length > 0;

  const mostrarAlerta = shouldShowCriticalAlert(
    porcentajeFranja,
    franja.porcentajeOcupacionEfectiva,
    franja.sinCapacidad,
  );

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          {hasDias && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mostrarAlerta && <WarningIcon color="error" fontSize="small" />}
            <Chip
              size="small"
              label={t(`prevision.franja.${franja.franjaHoraria}`)}
              variant="outlined"
            />
          </Box>
        </TableCell>
        <TableCell align="right">
          <ProfesoresCount
            count={franja.profesoresActivos}
            ids={franja.profesoresIds}
            label={`Profesores - ${t(`prevision.franja.${franja.franjaHoraria}`)}`}
          />
        </TableCell>
        <TableCell align="right">{franja.horasDisponibles.toFixed(1)}h</TableCell>
        <TableCell align="right">
          <Box sx={{ minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary">
              {t('prevision.dashboard.tabla.ocupacion')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {porcentajeFranja === null ? '—' : `${porcentajeFranja.toFixed(1)}%`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={porcentajeFranja === null ? 0 : Math.min(porcentajeFranja, 100)}
                color={getEstadoProgressColor(estadoFranja)}
                sx={{ flex: 1, height: 6, borderRadius: 1 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('prevision.dashboard.tabla.ocupacionEfectiva')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {porcentajeFranjaEfectiva === null
                  ? '—'
                  : `${porcentajeFranjaEfectiva.toFixed(1)}%`}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={
                  porcentajeFranjaEfectiva === null ? 0 : Math.min(porcentajeFranjaEfectiva, 100)
                }
                color="secondary"
                sx={{ flex: 1, height: 6, borderRadius: 1 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('prevision.dashboard.tabla.ocupacionAyuda')}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={t(`prevision.estado.${estadoFranja}`)}
            color={getEstadoChipColor(estadoFranja)}
          />
        </TableCell>
      </TableRow>
      {hasDias && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1, ml: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Typography variant="caption" fontWeight="bold">
                          Día
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight="bold">
                          Profesores
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight="bold">
                          Capacidad
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" fontWeight="bold">
                          Ocupación
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight="bold">
                          Estado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {franja.dias?.map((dia) => {
                      const porcentajeDia = dia.porcentajeOcupacion ?? null;
                      const porcentajeDiaEfectiva = dia.porcentajeOcupacionEfectiva ?? null;
                      const estadoDia = getEstadoCapacidad(
                        porcentajeDia,
                        porcentajeDiaEfectiva,
                        dia.sinCapacidad,
                      );

                      return (
                        <TableRow key={dia.diaSemana} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell component="th" scope="row">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {shouldShowCriticalAlert(
                                dia.porcentajeOcupacion,
                                dia.porcentajeOcupacionEfectiva,
                                dia.sinCapacidad,
                              ) && <WarningIcon color="error" fontSize="small" />}
                              <Typography variant="body2">{dia.diaSemana}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <ProfesoresCount
                              count={dia.profesoresActivos}
                              ids={dia.profesoresIds}
                              label={`Profesores - ${dia.diaSemana} - ${t(`prevision.franja.${franja.franjaHoraria}`)}`}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {dia.horasDisponibles.toFixed(1)}h
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ minWidth: 120 }}>
                              <Typography variant="caption" color="text.secondary">
                                {t('prevision.dashboard.tabla.ocupacion')}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontSize="0.75rem">
                                  {porcentajeDia === null ? '—' : `${porcentajeDia.toFixed(1)}%`}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={porcentajeDia === null ? 0 : Math.min(porcentajeDia, 100)}
                                  color={getEstadoProgressColor(estadoDia)}
                                  sx={{ flex: 1, height: 4, borderRadius: 1 }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5 }}
                              >
                                {t('prevision.dashboard.tabla.ocupacionEfectiva')}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontSize="0.75rem">
                                  {porcentajeDiaEfectiva === null
                                    ? '—'
                                    : `${porcentajeDiaEfectiva.toFixed(1)}%`}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    porcentajeDiaEfectiva === null
                                      ? 0
                                      : Math.min(porcentajeDiaEfectiva, 100)
                                  }
                                  color="secondary"
                                  sx={{ flex: 1, height: 4, borderRadius: 1 }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={t(`prevision.estado.${estadoDia}`)}
                              color={getEstadoChipColor(estadoDia)}
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

function CentroRow({ tipoId, centro, centroNombreReal, fechaBase }: CentroRowProps) {
  const { t } = useTranslation<'translation'>();
  const [open, setOpen] = useState(false);

  const { data: analisisHorario, isLoading } = useQuery({
    queryKey: ['prevision', 'horario-centro', tipoId, centro.centroId],
    queryFn: () =>
      previsionApi.getAnalisisHorario({
        tipoId,
        centroId: centro.centroId,
        fecha: fechaBase,
      }),
    enabled: open && (!centro.franjas || centro.franjas.length === 0),
  });

  const franjas =
    centro.franjas && centro.franjas.length > 0
      ? centro.franjas
      : analisisHorario?.analisisPorFranja?.map((f) => ({
          franjaHoraria: f.franjaHoraria,
          profesoresActivos: f.profesoresActivos,
          horasDisponibles: f.horasDisponibles,
          porcentajeOcupacion: f.horasDisponibles > 0 ? f.porcentajeOcupacion : null,
          sinCapacidad: f.horasDisponibles === 0 || f.profesoresActivos === 0,
        })) || [];

  const porcentajeCentro = centro.porcentajeOcupacion ?? null;
  const porcentajeCentroEfectiva = centro.porcentajeOcupacionEfectiva ?? null;
  const estadoCentro = getEstadoCapacidad(
    porcentajeCentro,
    porcentajeCentroEfectiva,
    centro.sinCapacidad,
  );

  const mostrarAlerta = shouldShowCriticalAlert(
    porcentajeCentro,
    centro.porcentajeOcupacionEfectiva,
    centro.sinCapacidad,
  );

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen((prev) => !prev)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mostrarAlerta && <WarningIcon color="error" fontSize="small" />}
            <Typography variant="body2">{centroNombreReal}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <ProfesoresCount
            count={centro.profesoresActivos}
            ids={centro.profesoresIds}
            label={`Profesores - ${centroNombreReal}`}
          />
        </TableCell>
        <TableCell align="right">
          {porcentajeCentro === null ? '—' : `${porcentajeCentro.toFixed(1)}%`}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={t(`prevision.estado.${estadoCentro}`)}
            color={getEstadoChipColor(estadoCentro)}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {t('prevision.dashboard.cargandoFranjas')}
                  </Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="50px"></TableCell>
                      <TableCell>{t('prevision.dashboard.tabla.franja')}</TableCell>
                      <TableCell align="right">
                        {t('prevision.dashboard.tabla.profesores')}
                      </TableCell>
                      <TableCell align="right">{t('prevision.dashboard.tabla.horas')}</TableCell>
                      <TableCell align="right">
                        {t('prevision.dashboard.tabla.ocupacion')}
                      </TableCell>
                      <TableCell>{t('prevision.dashboard.tabla.estadoCapacidad')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {franjas.map((franja) => (
                      <FranjaRow key={franja.franjaHoraria} franja={franja} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

// Componente auxiliar para mostrar número de profesores clickeable
interface ProfesoresCountProps {
  count: number;
  ids?: number[];
  label: string;
}

function ProfesoresCount({ count, ids, label }: ProfesoresCountProps) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!ids || ids.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
        <PeopleIcon fontSize="small" color="action" />
        {count}
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderRadius: 1,
          },
          px: 0.5,
          py: 0.25,
        }}
        onClick={() => setModalOpen(true)}
      >
        <PeopleIcon fontSize="small" color="primary" />
        <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
          {count}
        </Typography>
      </Box>
      <ProfesoresModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        profesoresIds={ids}
        titulo={label}
      />
    </>
  );
}

export default function PrevisionPage() {
  const { t } = useTranslation<'translation'>();
  const [tabIndex, setTabIndex] = useState(0);
  const [weekStart, setWeekStart] = useState(() => getWeekStart(dayjs()));

  // Cargar centros para obtener nombres correctos
  const { rows: centros } = useCentros();
  const centrosMap = new Map(centros.map((c) => [c.id, c.nombre]));

  // Query del dashboard
  const weekStartIso = weekStart.format('YYYY-MM-DD');
  const weekEnd = weekStart.add(7, 'day');

  const {
    data: dashboard,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['prevision', 'dashboard', weekStartIso],
    queryFn: () => previsionApi.getDashboard({ fecha: weekStartIso }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('prevision.dashboard.error')}
        {error instanceof Error && `: ${error.message}`}
      </Alert>
    );
  }

  const { alertasPendientes = [], resumenPorTipo = [] } = dashboard || {};

  // Contar alertas por severidad
  const alertasCriticas = alertasPendientes.filter((a) => a.severidad === 'critica').length;
  const alertasAltas = alertasPendientes.filter((a) => a.severidad === 'alta').length;
  const alertasMedias = alertasPendientes.filter((a) => a.severidad === 'media').length;

  // Calcular promedios
  const ocupacionPromedio =
    resumenPorTipo.length > 0
      ? resumenPorTipo.reduce(
          (sum, t) => sum + (t.porcentajeOcupacionEfectiva ?? t.porcentajeOcupacion ?? 0),
          0,
        ) / resumenPorTipo.length
      : 0;

  // Contar profesores ÚNICOS (un profesor puede estar en varios tipos)
  const profesoresUnicos = new Set<number>();
  resumenPorTipo.forEach((tipo) => {
    if (tipo.profesoresIds) {
      tipo.profesoresIds.forEach((id: number) => profesoresUnicos.add(id));
    }
  });
  const profesoresTotales = profesoresUnicos.size;

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5">{t('prevision.dashboard.titulo')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => setWeekStart((prev) => prev.add(-7, 'day'))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {weekStart.format('D MMM')} - {weekEnd.format('D MMM')}
          </Typography>
          <IconButton size="small" onClick={() => setWeekStart((prev) => prev.add(7, 'day'))}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {/* KPIs superiores */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                {t('prevision.dashboard.kpi.alertasCriticas')}
              </Typography>
              <Typography variant="h4" color="error.main">
                {alertasCriticas}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                {t('prevision.dashboard.kpi.alertasAltas')}
              </Typography>
              <Typography variant="h4" color="warning.main">
                {alertasAltas}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                {t('prevision.dashboard.kpi.ocupacionPromedio')}
              </Typography>
              <Typography variant="h4" color="primary.main">
                {ocupacionPromedio.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                {t('prevision.dashboard.kpi.profesoresActivos')}
              </Typography>
              <Typography variant="h4">{profesoresTotales}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabIndex} onChange={(_, newValue) => setTabIndex(newValue)}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('prevision.dashboard.tabs.alertas')}
                {alertasPendientes.length > 0 && (
                  <Chip size="small" label={alertasPendientes.length} color="error" />
                )}
              </Box>
            }
          />
          <Tab label={t('prevision.dashboard.tabs.resumenTipos')} />
          <Tab label={t('prevision.dashboard.tabs.porFranja')} />
        </Tabs>

        {/* Tab 1: Alertas Pendientes */}
        <TabPanel value={tabIndex} index={0}>
          {alertasPendientes.length === 0 ? (
            <Alert severity="success">{t('prevision.dashboard.alertas.sinAlertas')}</Alert>
          ) : (
            <Stack spacing={2}>
              {alertasPendientes.map((alerta) => (
                <Paper key={alerta.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {alerta.direccion === 'insuficiencia' ? (
                        <WarningIcon color="error" />
                      ) : (
                        <TrendingDownIcon color="warning" />
                      )}
                      <Typography variant="subtitle1">
                        {alerta.tipoAsignatura?.descripcion || `Tipo ${alerta.idTipoAsignatura}`}
                        {alerta.centro && ` - ${alerta.centro.nombre}`}
                      </Typography>
                      <Chip
                        size="small"
                        label={t(`prevision.severidad.${alerta.severidad}`)}
                        color={
                          alerta.severidad === 'critica'
                            ? 'error'
                            : alerta.severidad === 'alta'
                              ? 'warning'
                              : 'info'
                        }
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {alerta.descripcion}
                    </Typography>

                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('prevision.dashboard.alertas.profesoresDif')}:{' '}
                        <strong>
                          {alerta.profesoresDiferencia > 0 ? '+' : ''}
                          {alerta.profesoresDiferencia}
                        </strong>
                      </Typography>
                      {alerta.franjaHoraria && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                          {t('prevision.dashboard.alertas.franja')}:{' '}
                          {t(`prevision.franja.${alerta.franjaHoraria}`)}
                        </Typography>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      {t('prevision.dashboard.alertas.generada')}:{' '}
                      {dayjs(alerta.fechaGeneracion).format('DD/MM/YYYY HH:mm')}
                    </Typography>

                    {alerta.accionesRecomendadas && alerta.accionesRecomendadas.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {t('prevision.dashboard.alertas.acciones')}:
                        </Typography>
                        <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                          {alerta.accionesRecomendadas.map((accion, idx) => (
                            <li key={idx}>
                              <Typography variant="caption">{accion}</Typography>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </TabPanel>

        {/* Tab 2: Resumen por Tipo */}
        <TabPanel value={tabIndex} index={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>{t('prevision.dashboard.tabla.tipo')}</TableCell>
                <TableCell align="right">{t('prevision.dashboard.tabla.profesores')}</TableCell>
                <TableCell align="right">{t('prevision.dashboard.tabla.ocupacion')}</TableCell>
                <TableCell>{t('prevision.dashboard.tabla.estadoCapacidad')}</TableCell>
                <TableCell align="right">{t('prevision.dashboard.tabla.demanda')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resumenPorTipo.map((tipo) => (
                <TipoRow
                  key={tipo.tipoAsignaturaId}
                  tipo={tipo}
                  fechaBase={weekStartIso}
                  centrosMap={centrosMap}
                />
              ))}
            </TableBody>
          </Table>
        </TabPanel>

        {/* Tab 3: Desglose por Franja Horaria */}
        <TabPanel value={tabIndex} index={2}>
          <Stack spacing={3}>
            {resumenPorTipo.map((tipo) => (
              <Paper key={tipo.tipoAsignaturaId} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {shouldShowCriticalAlert(
                    tipo.porcentajeOcupacion,
                    tipo.porcentajeOcupacionEfectiva,
                    tipo.sinCapacidad,
                  ) && <WarningIcon color="error" fontSize="small" />}
                  <Typography variant="h6">{tipo.tipoAsignaturaNombre}</Typography>
                </Box>

                {tipo.desglosePorCentro && tipo.desglosePorCentro.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={48} />
                        <TableCell>{t('prevision.dashboard.tabla.centro')}</TableCell>
                        <TableCell align="right">
                          {t('prevision.dashboard.tabla.profesores')}
                        </TableCell>
                        <TableCell align="right">
                          {t('prevision.dashboard.tabla.ocupacion')}
                        </TableCell>
                        <TableCell>{t('prevision.dashboard.tabla.estadoCapacidad')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tipo.desglosePorCentro.map((centro) => (
                        <CentroRow
                          key={`${tipo.tipoAsignaturaId}-${centro.centroId}`}
                          tipoId={tipo.tipoAsignaturaId}
                          centro={centro}
                          centroNombreReal={centrosMap.get(centro.centroId) || centro.centroNombre}
                          fechaBase={weekStartIso}
                        />
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('prevision.dashboard.tabla.franja')}</TableCell>
                        <TableCell align="right">
                          {t('prevision.dashboard.tabla.profesores')}
                        </TableCell>
                        <TableCell align="right">{t('prevision.dashboard.tabla.horas')}</TableCell>
                        <TableCell align="right">
                          {t('prevision.dashboard.tabla.ocupacion')}
                        </TableCell>
                        <TableCell>{t('prevision.dashboard.tabla.estadoCapacidad')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tipo.desglosePorFranja?.map((franja) => {
                        const porcentajeFranja = franja.porcentajeOcupacion ?? null;
                        const porcentajeFranjaEfectiva = franja.porcentajeOcupacionEfectiva ?? null;
                        const estado = getEstadoCapacidad(
                          porcentajeFranja,
                          porcentajeFranjaEfectiva,
                          franja.sinCapacidad,
                        );

                        const mostrarAlertaFranja = shouldShowCriticalAlert(
                          franja.porcentajeOcupacion,
                          franja.porcentajeOcupacionEfectiva,
                          franja.sinCapacidad,
                        );

                        return (
                          <TableRow key={franja.franjaHoraria}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {mostrarAlertaFranja && (
                                  <WarningIcon color="error" fontSize="small" />
                                )}
                                <Chip
                                  size="small"
                                  label={t(`prevision.franja.${franja.franjaHoraria}`)}
                                  variant="outlined"
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="right">{franja.profesoresActivos}</TableCell>
                            <TableCell align="right">
                              {franja.horasDisponibles.toFixed(1)}h
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ minWidth: 80 }}>
                                <Typography variant="body2">
                                  {porcentajeFranja === null
                                    ? '—'
                                    : `${porcentajeFranja.toFixed(1)}%`}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    porcentajeFranja === null ? 0 : Math.min(porcentajeFranja, 100)
                                  }
                                  color={getEstadoProgressColor(estado)}
                                  sx={{ mt: 0.5 }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={t(`prevision.estado.${estado}`)}
                                color={getEstadoChipColor(estado)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            ))}
          </Stack>
        </TabPanel>
      </Paper>
    </Stack>
  );
}
